import ProtectedRoute from "@/components/layout/ProtectedRoute";
import Screen from "@/components/layout/Screen";
import { Card } from "@/components/shared/Card";
import PassCard, { isPassExpired } from "@/components/shared/InfoCard/PassCard";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { HStack } from "@/components/ui/hstack";
import { SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { usePasses } from "@/hooks/usePasses";
import { useT } from "@transifex/react";
import { useFocusEffect, usePathname } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { Pressable } from "react-native";

const MyPasses = () => {
  const [isActivePasses, setIsActivePasses] = useState(true);
  const t = useT();
  const pathName = usePathname();

  const { passes, fetchStatus, refetch } = usePasses();

  const { activePasses, expiredPasses } = useMemo(() => {
    if (!passes) return { activePasses: [], expiredPasses: [] };

    return {
      activePasses: passes.filter((pass) => !isPassExpired(pass.validTo)),
      expiredPasses: passes.filter((pass) => isPassExpired(pass.validTo)),
    };
  }, [passes]);

  const togglePassesView = () => setIsActivePasses((prev) => !prev);

  const renderPassList = () => {
    const currentPasses = isActivePasses ? activePasses : expiredPasses;

    if (fetchStatus === "fetching") {
      return (
        <Card>
          <SkeletonText className="h-[22px] w-[200px] mx-auto" />
        </Card>
      );
    }

    if (currentPasses.length === 0) {
      return (
        <Card>
          <Text className="font-[600] leading-[22px] text-[16px] text-center text-typography-800">
            {isActivePasses ? t("No active passes found") : t("No expired passes found")}
          </Text>
        </Card>
      );
    }

    return currentPasses.map((pass) => <PassCard key={pass.passID} pass={pass} />);
  };

  useFocusEffect(
    useCallback(() => {
      // Avoid refetch on other screens, the focus effect is called
      // with the prevous pathname as well.
      if (pathName.startsWith("/my-passes")) {
        refetch();
      }
    }, [pathName, refetch]),
  );

  return (
    <ProtectedRoute>
      <Screen>
        {fetchStatus === "fetching" ? <SkeletonText className="h-[24px] w-[220px] mx-auto" /> : <PageTitle title={t("My Passes")} />}
        <HStack className="justify-between">
          {fetchStatus === "fetching" ? (
            <>
              <SkeletonText className="h-[22px] w-[120px]" />
              <SkeletonText className="h-[22px] w-[140px]" />
            </>
          ) : (
            <>
              <Text className="font-[700] leading-[22px] text-[16px] text-typography-900">{isActivePasses ? t("Active Passes") : t("Expired Passes")}</Text>
              <Pressable onPress={togglePassesView}>
                <Text className="font-[400] leading-[22px] text-[16px] text-typography-900 underline">
                  {!isActivePasses ? t("View active passes") : t("View expired passes")}
                </Text>
              </Pressable>
            </>
          )}
        </HStack>
        {renderPassList()}
      </Screen>
    </ProtectedRoute>
  );
};

export default MyPasses;
